// AboutPage.jsx
import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { useLanguage } from "./LanguageContext";

const brand = {
  primary: "#a15a5a",
  dark: "#2d2d2d",
  light: "#f8f8f8",
  text: "#333",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Layout */
const Page = styled.div`
  width: 100%;
  color: ${brand.text};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const Hero = styled.section`
  background: linear-gradient(180deg, #ffffff 0%, ${brand.light} 100%);
  border-radius: 16px;
  padding: 48px 28px;
  display: grid;
  gap: 28px;
  align-items: left;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: left;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(28px, 4vw, 40px);
  margin: 0 0 12px;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(16px, 2.4vw, 18px);
  opacity: 0.9;
  margin: 0 0 18px;
`;

const HeroBadge = styled.span`
  display: inline-block;
  background: ${brand.primary};
  color: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 12px;
  letter-spacing: .3px;
  margin-bottom: 14px;
  margin-left: -30px;
`;

const HeroImage = styled.div`
  border-radius: 16px;
  background: #fff;
  border: 1px solid #eee;
  min-height: 220px;
  display: grid;
  place-items: center;
  animation: ${fadeUp} .6s ease both;
  box-shadow: 0 6px 24px rgba(0,0,0,.06);

  /* Placeholder – swap with a brand image if you want */
  &::before{
    content: "Shopaholics";
    font-weight: 700;
    font-size: 20px;
    color: ${brand.primary};
  }
`;

const Section = styled.section`
  margin-top: 40px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(22px, 3vw, 28px);
  margin: 0 0 16px;
`;

const Lead = styled.p`
  font-size: 16px;
  opacity: .9;
  max-width: 820px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 6px 20px rgba(0,0,0,.04);
  animation: ${fadeUp} .5s ease both;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 6px;
`;

const CardText = styled.p`
  font-size: 14px;
  opacity: .9;
  margin: 0;
`;

const List = styled.ul`
  margin: 10px 0 0 18px;
  li{ margin: 6px 0; }
`;

const Highlight = styled.span`
  color: ${brand.primary};
  font-weight: 600;
`;

const Values = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0,1fr));
  gap: 12px;
  margin-top: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ValueItem = styled.div`
  background: ${brand.light};
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 14px;
  font-size: 14px;

  strong{ color: ${brand.dark}; }
`;

const CTA = styled.div`
  background: ${brand.dark};
  color: #fff;
  border-radius: 16px;
  padding: 22px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: center;
  margin-top: 26px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const CTAButton = styled(Link)`
  background: #fff;
  color: ${brand.dark};
  border: 0;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid transparent;

  &:hover{
    border-color: #fff;
    background: transparent;
    color: #fff;
  }
`;

export default function AboutPage() {
  const { language } = useLanguage();
  const isEN = language === "en";

  useEffect(() => {
    document.title = isEN ? "About Us • Shopaholics" : "ჩვენს შესახებ • Shopaholics";
  }, [isEN]);

  const ka = {
    badge: "ჩვენს შესახებ",
    title: "სურნელებისა და მოვლის ონლაინ სივრცე",
    subtitle:
      "ვკრებთ სანდო ბრენდების პარფიუმერიას, კანისა და თმის მოვლის პროდუქტებს და გთავაზობთ აქსესუარებს გამჭვირვალე ფასებით და მოსახერხებელი სერვისით.",
    offerTitle: "რას გთავაზობთ",
    cards: [
      {
        t: "პარფიუმერია",
        d: "კლასიკური და ნიშური სურნელები სხვადასხვა მოცულობით — თქვენი ყოველდღიურობისა და განსაკუთრებული შემთხვევებისთვის.",
      },
      {
        t: "კანისა და თმის მოვლა",
        d: "ყოველდღიური რუტინა და მიზნობრივი ქურები — სისუფთავე, დატენიანება და დაცვა ერთ სივრცეში.",
      },
      {
        t: "აქსესუარები",
        d: (
          <>
            <Highlight>უმაღლესი ნატურალური ტყავით</Highlight> დამზადებული
            ხელნაკეთი აქსესუარები — <strong>ქამრები</strong>,{" "}
            <strong>სამაჯურები</strong>, <strong>საბარათეები</strong>,{" "}
            <strong>საფულეები</strong> და სხვა. დეტალები, რომლებიც სტილს
            განსაზღვრავს.
          </>
        ),
      },
    ],
    whyTitle: "რატომ Shopaholics",
    whyLead:
      "გუნდის მიზანია, ხარისხიანი და ავთენტური პროდუქტი იყოს ხელმისაწვდომი სამართლიან ფასად და სწორი ინფორმაციით.",
    values: [
      "ავთენტურობა და ხარისხი — მხოლოდ სანდო დისტრიბუტორები",
      "სამართლიანი ფასები და გამჭვირვალე შეთავაზებები",
      "სწრაფი მიწოდება საქართველოს მასშტაბით",
      "უსაფრთხო გადახდა პარტნიორი ბანკის დაცულ გვერდზე",
    ],
    promiseTitle: "ჩვენი დაპირება",
    promise:
      "გაგიზიარებთ დეტალურ აღწერებსა და მომხმარებელთა მიმოხილვებს, დაგეხმარებით თქვენი სურნელისა და მოვლის რუტინის არჩევაში.",
    ctaText: "გაქვთ შეკითხვა ან გინდათ რეკომენდაცია? დაგვიკავშირდით და გაგიწევთ მეგზურობას.",
    ctaBtn: "საკონტაქტო გვერდი",
  };

  const en = {
    badge: "About Us",
    title: "your space for fragrance & care",
    subtitle:
      "We bring together trusted brands in perfumery, skin & hair care, plus accessories — with transparent pricing and a smooth experience.",
    offerTitle: "What we offer",
    cards: [
      {
        t: "Perfumery",
        d: "From timeless icons to niche scents, in multiple sizes—made for everyday wear and special moments.",
      },
      {
        t: "Skin & Hair Care",
        d: "Daily essentials and targeted treatments to cleanse, hydrate and protect—simple, effective routines.",
      },
      {
        t: "Accessories",
        d: (
          <>
            <Highlight>Premium natural leather</Highlight> handmade
            accessories — <strong>belts</strong>, <strong>bracelets</strong>,{" "}
            <strong>card holders</strong>, <strong>wallets</strong>, and more.
            Finishing details that define your style.
          </>
        ),
      },
    ],
    whyTitle: "Why Shopaholics",
    whyLead:
      "Our goal is to make authentic, quality products accessible—with fair pricing and clear information.",
    values: [
      "Authenticity & quality — sourced from reliable distributors",
      "Fair prices & transparent offers",
      "Fast nationwide delivery in Georgia",
      "Secure checkout via our partner bank’s protected page",
    ],
    promiseTitle: "Our promise",
    promise:
      "We share clear product details and real feedback—and help you find a scent wardrobe and care routine you’ll love.",
    ctaText:
      "Questions or need a recommendation? Reach out and we’ll guide you.",
    ctaBtn: "Contact page",
  };

  const c = isEN ? en : ka;

  return (
    <Page>
      <Container>
        <Hero>
          <div>
            <HeroBadge>{c.badge}</HeroBadge>
            <HeroTitle>{c.title}</HeroTitle>
            <HeroSubtitle>{c.subtitle}</HeroSubtitle>
          </div>
        </Hero>

        <Section>
          <SectionTitle>{c.offerTitle}</SectionTitle>
          <Grid>
            {c.cards.map((card, i) => (
              <Card key={i}>
                <CardTitle>{card.t}</CardTitle>
                <CardText>{card.d}</CardText>
              </Card>
            ))}
          </Grid>
        </Section>

        <Section>
          <SectionTitle>{c.whyTitle}</SectionTitle>
          <Lead>{c.whyLead}</Lead>
          <Values>
            {c.values.map((v, i) => (
              <ValueItem key={i}>
                <strong>•</strong> {v}
              </ValueItem>
            ))}
          </Values>
        </Section>

        <Section>
          <SectionTitle>{c.promiseTitle}</SectionTitle>
          <Lead>{c.promise}</Lead>

          <CTA>
            <div>{c.ctaText}</div>
          </CTA>
        </Section>
      </Container>
    </Page>
  );
}
